// Archived properties, so whatever still refers to them keeps its name. The
// office's property list (useAssignableProperties) leaves archived ones out,
// since nothing new is booked on them; their costs and settlements stay, and
// are named by their address, marked as archived.

import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { EPropertyTypeId } from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { useMemo } from "react";
import { uniqueById } from "./costsList.utils";
import {
  addressPartsOf,
  IUnitProperty,
  unitDisplayLabel,
  unknownPropertyIds,
} from "./groupUnits.utils";

interface IWithArchivedPropertiesParams {
  properties: IUnitProperty[];
  isFetching?: boolean;
  ids: (string | undefined)[];
  // Mark an archived property's label (the default, for the screens); a
  // statement names the address only.
  marked?: boolean;
}

// `properties` plus the properties among `ids` it does not hold, fetched by
// id. Asked only once `properties` is in: before that every id is unknown.
// `isResolving` holds while either list is still on its way, so a caller
// that needs one of `ids` (a unit's group) can wait for it.
export const useWithArchivedPropertiesState = (
  params: IWithArchivedPropertiesParams,
): { properties: IUnitProperty[]; isResolving: boolean } => {
  const { properties, isFetching = false, ids, marked = true } = params;
  const isLoaded = !isFetching && properties.length > 0;
  const missingKey = (
    isLoaded ? unknownPropertyIds({ properties, ids }) : []
  ).join(",");

  const query = useMemo(() => {
    if (!missingKey) return undefined;
    const byId = missingKey.split(",").map(id => ({ id: { eq: Number(id) } }));
    return { filter: byId.length === 1 ? byId[0] : { or: byId } };
  }, [missingKey]);
  const {
    items,
    isFetching: isFetchingMissing,
    fetchError,
  } = propertyHooks.useGetAll({ shouldRefetch: false, query });

  const withArchived = useMemo(() => {
    if (!query || !items || items.length === 0) return properties;
    const known = new Set(properties.map(property => property.id));
    const extra: IUnitProperty[] = uniqueById(items)
      .filter(item => !known.has(String(item.id)))
      .map(item => {
        const address = formatAddress(item) || item.name || String(item.id);
        const label = marked
          ? unitDisplayLabel({
              label: address,
              archivedOn: item.archivedAt ? String(item.archivedAt) : undefined,
            })
          : address;
        return {
          id: String(item.id),
          code: label,
          address: label,
          type: item.typeId === EPropertyTypeId.Group ? "building" : "unit",
          parentId: item.parentId != null ? String(item.parentId) : undefined,
          box: item.box?.trim() || undefined,
          addressParts: addressPartsOf(item),
          ...(item.brokerId != null ? { brokerId: Number(item.brokerId) } : {}),
        };
      });
    return extra.length > 0 ? [...properties, ...extra] : properties;
  }, [properties, items, query, marked]);

  const isResolving =
    isFetching ||
    (!!query && (isFetchingMissing || (items === undefined && !fetchError)));

  return { properties: withArchived, isResolving };
};

export const useWithArchivedProperties = (
  params: IWithArchivedPropertiesParams,
): IUnitProperty[] => useWithArchivedPropertiesState(params).properties;
